import { Component, ComponentRef, Inject, Input, OnChanges, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { CONSIGNMENT_INFO_ALLOWED_PATTERN, DEPOSIT_ARTICLE_NUMBER } from '@shared/models/shared.model';
import { CartAndCheckoutLoadingService } from '@shared/services/cart-and-checkout-loading.service';
import { ActiveCartFacade, Cart, DeliveryMode, MultiCartFacade, OrderEntry } from '@spartacus/cart/base/root';
import { CheckoutPlaceOrderComponent } from '@spartacus/checkout/base/components';
import { Address, Config, RoutingService, TranslatePipe } from '@spartacus/core';
import { OrderDetailsService } from '@spartacus/order/components';
import { Order, OrderFacade, ReplenishmentOrder } from '@spartacus/order/root';
import { LAUNCH_CALLER, LaunchDialogService, FormErrorsComponent } from '@spartacus/storefront';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, first, map, withLatestFrom } from 'rxjs/operators';
import { ProductCategory } from '../../../cart-administration/model/cart-administration-enum.model';
import { OrderInfoFieldValue, OrderType } from '../../../cart-administration/model/cart-administration.model';
import { UpdateCartArg } from '../../../cart-administration/model/update-cart.model';
import { B2BUserAccountService } from '../../../cart-administration/services/b2-b-user-account.service';
import { CartParametersService } from '../../../cart-administration/services/cart-parameters.service';
import { UpdateCartService } from '../../../cart-administration/services/update-cart.service';
import { OrderOverviewService } from '../../../order-history/services/order-overview.service';
import { CheckoutOrderUiService } from '../../service/checkout-order-ui.service';
import { OrderInfoFieldsFormsValidationService } from '../../service/order-info-fields-forms-validation.service';
import { PermissionCheckService } from '../../../user-profiles/service/permission-check.service';
import { CUSTOM_LAUNCH_CALLER } from '../../../launch-caller/launch-caller-enum.model';
import { NgClass, AsyncPipe } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

const LEFT_PARENTHESIS = '(';
const RIGHT_PARENTHESIS = ')';
const OR_CONJUNCTION = '|';
const ORDER_SUMMARY_ORDER_INFO_FIELDS_FORM_KEY = 'ORDER_SUMMARY_ORDER_INFO_FIELDS';

@Component({
    selector: 'app-efa-place-order',
    templateUrl: './efa-place-order.component.html',
    imports: [FormsModule, ReactiveFormsModule, NgClass, NgbTooltip, FormErrorsComponent, AsyncPipe, TranslatePipe]
})
export class EfaPlaceOrderComponent extends CheckoutPlaceOrderComponent implements OnInit, OnChanges, OnDestroy {
  private mySubscription: Subscription = new Subscription();
  private displayCustomerPrices: boolean;

  @Input() orderInfoFields: OrderInfoFieldValue[];
  @Input() orderType: OrderType;
  @Input() deliveryMode: DeliveryMode;
  @Input() deliveryAddress: Address;
  @Input() requestedDeliveryDate: string;
  @Input() readonly: boolean;
  @Input() deactivatePlaceOrder: boolean;

  checkoutSubmitForm: UntypedFormGroup = this.fb.group({
    consignmentInfo: ['', [Validators.pattern(CONSIGNMENT_INFO_ALLOWED_PATTERN), Validators.required]]
  });
  orderDetails$: Observable<Order | ReplenishmentOrder> = this.orderDetailsService.getOrderDetails();
  cart$: Observable<Cart> = this.activeCartService.getActive();
  placeOrderRunning$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  orderEntries: OrderEntry[];
  readonly consignmentInfoAllowedMaxLength: number = 20;

  constructor(
    protected orderFacade: OrderFacade,
    protected routingService: RoutingService,
    protected fb: UntypedFormBuilder,
    protected launchDialogService: LaunchDialogService,
    protected vcr: ViewContainerRef,
    protected b2bUserAccountService: B2BUserAccountService,
    private updateCartService: UpdateCartService,
    private checkoutOrderUiService: CheckoutOrderUiService,
    private orderOverviewService: OrderOverviewService,
    private cartAndCheckoutLoadingService: CartAndCheckoutLoadingService,
    private orderInfoFieldsFormsValidationService: OrderInfoFieldsFormsValidationService,
    private multiCartService: MultiCartFacade,
    private cartParametersService: CartParametersService,
    protected orderDetailsService: OrderDetailsService,
    private activeCartService: ActiveCartFacade,
    private permissionCheckService: PermissionCheckService,
    @Inject(Config) private config: any,
  ) {
    super(orderFacade, routingService, fb, launchDialogService, vcr);
  }

  ngOnChanges(): void {
    if (!!this.orderInfoFields) {
      this.orderInfoFields.forEach((orderInfoFields: OrderInfoFieldValue) => {
        if (orderInfoFields.orderInfoField) {
          const formControl: UntypedFormControl = new UntypedFormControl('');
          this.addOrderInfoFieldFormControlValidators(orderInfoFields, formControl);
          this.checkoutSubmitForm.addControl(orderInfoFields.orderInfoField.fieldId, formControl);
        }
      });
    }
  }

  ngOnInit(): void {
    this.orderInfoFieldsFormsValidationService.reset();
    this.orderInfoFieldsFormsValidationService.add(ORDER_SUMMARY_ORDER_INFO_FIELDS_FORM_KEY, this.checkoutSubmitForm);
    this.mySubscription.add(
      this.cart$
        .pipe(
          filter(Boolean),
          first(),
          map((cart: Cart) => cart.entries)
        )
        .subscribe((entries: OrderEntry[]) => (this.orderEntries = entries))
    );
    
    this.mySubscription.add(
      this.permissionCheckService.displayCustomerPrices().subscribe((dc: boolean) => this.displayCustomerPrices = dc)
    );

    if (this.checkoutSubmitForm.valid) {
      this.checkoutOrderUiService.setSelectedIsValidForm(true);
    }

    this.checkoutSubmitForm.statusChanges.subscribe((status: string) => {
      if (status === 'VALID') {
        this.checkoutOrderUiService.setSelectedIsValidForm(true);
      } else {
        this.checkoutOrderUiService.setSelectedIsValidForm(false);
      }
    });

    this.mySubscription.add(
      this.checkoutOrderUiService.selectedPlaceOrderTriggered().pipe(filter(Boolean)).subscribe(value => this.submitForm()
      ));

    this.mySubscription.add(
      this.updateCartService.success().pipe(withLatestFrom(this.placeOrderRunning$),
        distinctUntilChanged(([a, b]) => (!a[1] && b[1])),
        filter(([a, b]) => a && b))
      .subscribe(b => {
        this.placedOrder = this.launchDialogService.launch(
          LAUNCH_CALLER.PLACE_ORDER_SPINNER,
          this.vcr
        );

        this.orderFacade.placeOrder(true).subscribe({
          error: () => {
            if (!this.placedOrder) {
              return;
            }

            this.placedOrder
              .subscribe((component: ComponentRef<any>) => {
                this.launchDialogService.clear(LAUNCH_CALLER.PLACE_ORDER_SPINNER);
                if (component) {
                  component.destroy();
                }
              })
              .unsubscribe();
          },
          next: () => this.onSuccess(),
        });

        this.checkoutOrderUiService.resetTriggerPlaceOrder();
        this.placeOrderRunning$.next(false);
        this.orderOverviewService.resetOverview();
      }));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.orderInfoFieldsFormsValidationService.remove(ORDER_SUMMARY_ORDER_INFO_FIELDS_FORM_KEY);
    this.cartAndCheckoutLoadingService.setCartItemListLoading(false);
    this.mySubscription.unsubscribe();
  }

  submitForm(): void {
    if (this.checkoutSubmitForm.valid) {
      const requestedDeliveryDate: Date = new Date(this.requestedDeliveryDate);
      const data: UpdateCartArg = {
        cartId: this.b2bUserAccountService.cartId,
        userId: this.b2bUserAccountService.userId,
        orderTypeId: this.orderType.id,
        shippingTypeId: this.deliveryMode.code,
        addressId: this.deliveryAddress.id,
        calculate: false,
        requestedDeliveryDate: requestedDeliveryDate.getFullYear() + '-'
          + (requestedDeliveryDate.getMonth() + 1) + '-'
          + requestedDeliveryDate.getDate(),
        consignmentInfo: this.checkoutSubmitForm.value.consignmentInfo,
        orderInfoFields: this.getOrderInfoField()
      };
      this.updateCartService.reset();
      this.placeOrderRunning$.next(true);
      this.updateCartService.updateCart(data);
    }
  }

  getOrderInfoField(): OrderInfoFieldValue[] {
    const orderInfoFieldValue: OrderInfoFieldValue[] = [];
    this.orderInfoFields.forEach((orderInfoFields: OrderInfoFieldValue) => {
      const object = {
        orderInfoField: orderInfoFields.orderInfoField,
        value: this.checkoutSubmitForm.controls[orderInfoFields.orderInfoField.fieldId].value
      };
      orderInfoFieldValue.push(object);
    });
    return orderInfoFieldValue;
  }

  private addOrderInfoFieldFormControlValidators(orderInfoField: OrderInfoFieldValue, formControl: UntypedFormControl): void {
    if (orderInfoField.orderInfoField.mandatory) {
      formControl.addValidators(Validators.required);
    } else {
      formControl.addValidators(Validators.nullValidator);
    }

    const validOrderInfoFieldLengthRange: boolean = this.validOrderInfoFieldLengthRange(orderInfoField);

    if (validOrderInfoFieldLengthRange) {
      if (orderInfoField.orderInfoField.numeric) {
        this.addNumericOrderInfoFieldFormControlValidators(orderInfoField, formControl);
      } else {
        this.addTextOrderInfoFieldFormControlValidators(orderInfoField, formControl);
      }
    }
  }

  private addTextOrderInfoFieldFormControlValidators(orderInfoField: OrderInfoFieldValue, formControl: UntypedFormControl): void {
    if (orderInfoField.orderInfoField.exactLength) {
      formControl.addValidators(
        [Validators.minLength(orderInfoField.orderInfoField.length)]);
    } else {
      formControl.addValidators(Validators.maxLength(orderInfoField.orderInfoField.length));
    }

    if (orderInfoField.orderInfoField.patterns?.length > 0) {
      const patternsInConjuctionForm: string = orderInfoField.orderInfoField.patterns.map(
        (pattern: string) => LEFT_PARENTHESIS + pattern + RIGHT_PARENTHESIS).join(OR_CONJUNCTION);
      formControl.addValidators(Validators.pattern(patternsInConjuctionForm));
    }
  }

  private addNumericOrderInfoFieldFormControlValidators(orderInfoField: OrderInfoFieldValue, formControl: UntypedFormControl): void {
    if (orderInfoField.orderInfoField.exactLength) {
      formControl.addValidators(
        [Validators.max(this.getMaxNumberForNumericLength(orderInfoField.orderInfoField.length)),
          Validators.min(this.getMinNumberForNumericLength(orderInfoField.orderInfoField.length))]);
    } else {
      formControl.addValidators(
        [Validators.max(this.getMaxNumberForNumericLength(orderInfoField.orderInfoField.length)),
          Validators.min(0)]);
    }
  }

  private validOrderInfoFieldLengthRange(orderInfoField: OrderInfoFieldValue): boolean {
    return orderInfoField.orderInfoField.length != null && orderInfoField.orderInfoField.length > 0;
  }

  private getMaxNumberForNumericLength(length: number): number {
    let nom = '9';
    for (let i = 0; i < length - 1; i++) {
      nom += '9';
    }
    return Number.parseInt(nom, 10);
  }

  private getMinNumberForNumericLength(length: number): number {
    let nom = '1';
    for (let i = 0; i < length - 1; i++) {
      nom += '0';
    }
    return Number.parseInt(nom, 10);
  }

  onSuccess(): void {
    super.onSuccess();
    const exceedingLimitOrderEntry: OrderEntry = this.getExceedingLimitOrderEntry(this.orderEntries);

    if (
      this.config.showOrderLimitExceededConfirmationDialog &&
      exceedingLimitOrderEntry != null
    ) {
      this.launchDialogService.openDialogAndSubscribe(
        CUSTOM_LAUNCH_CALLER.ORDER_LIMIT_EXCEEDED
      );
    }

    this.multiCartService.createCart(
      {
        userId: this.b2bUserAccountService.userId,
        extraData: {active: true}
      });
    this.cartParametersService.setCurrentProductCategory(ProductCategory.SPARE_PARTS);
  }

  private getExceedingLimitOrderEntry(orderEntries: OrderEntry[]): OrderEntry {
    for (let i = 0; i < orderEntries.length; i++) {
      const orderEntry = orderEntries[i];
      if (
        (orderEntry.subEntry &&
          orderEntry.product?.manufacturerAID === DEPOSIT_ARTICLE_NUMBER) ||
        orderEntry.parent
      ) {
        continue;
      }

      let totalPrice: number = this.getTotalPriceValueForOrderEntry(orderEntry);

      orderEntry?.subEntries
        ?.filter(
          (subEntry: OrderEntry) =>
            subEntry.product?.manufacturerAID !== DEPOSIT_ARTICLE_NUMBER
        )
        .forEach((subEntry: OrderEntry) => {
          totalPrice =
            totalPrice + this.getTotalPriceValueForOrderEntry(subEntry);
        });

      let predecessorEntryIndex: number = i + 1;
      while (predecessorEntryIndex < orderEntries.length) {
        let predecessorOrderEntry: OrderEntry =
          orderEntries[predecessorEntryIndex];
        if (predecessorOrderEntry.parent) {
          totalPrice =
            totalPrice +
            this.getTotalPriceValueForOrderEntry(predecessorOrderEntry);
        } else {
          break;
        }

        predecessorEntryIndex = predecessorEntryIndex + 1;
      }

      if (totalPrice > 9999) {
        return orderEntry;
      }
    }
    return undefined;
  }

  private getTotalPriceValueForOrderEntry(orderEntry: OrderEntry): number {
    return this.displayCustomerPrices
      ? orderEntry.customerTotalPrice?.value
      : orderEntry.totalPrice?.value;
  }
}
